// One turn of a chat (D82): the prompt with its ticks (sent when the relay took it, delivered
// when the worker echoed it), a chip with the turn's state and CLI·model (and Cancel while it is
// in flight), its working output folded away, and the agent's reply.
import { EventRow } from "../conversation/EventRow";
import { useT } from "../i18n";
import { Pill } from "../ui/status";
import type { Tone } from "../ui/tones";
import { ACTIVE, type Turn, type TurnStatus } from "./api";
import { AgentMessage, type Delivery, UserMessage } from "./Bubbles";
import { CLIS } from "./clis";
import { agentText, type PendingPrompt, type TurnEntry, userText } from "./model";
import { TurnProgress } from "./TurnProgress";

const TONE: Record<TurnStatus, Tone> = { queued: "gray", starting: "blue", running: "blue", done: "green", failed: "red", cancelled: "gray" };

function cliLabel(turn: Turn): string {
  const cli = CLIS.find((c) => c.value === turn.cli)?.label ?? turn.cli;
  return turn.model ? `${cli} · ${turn.model}` : cli;
}

function deliveryOf(entry: TurnEntry, mine: PendingPrompt | undefined): Delivery {
  if (entry.echo) return "delivered";
  if (mine?.state === "failed") return "failed";
  if (entry.prompt) return "sent";
  return mine?.state ?? "waiting";
}

const plain = (e: TurnEntry["echo"]): string | undefined => (e?.content.kind === "plaintext" ? e.content.text : undefined);

export interface TurnBlockProps {
  entry: TurnEntry;
  /** This device's prompt for the turn, while it is on its way. */
  mine?: PendingPrompt;
  /** Why a queued turn without a run waits (a task's running step, or the turn before). */
  waitHint: string;
  onCancel?: () => void;
}

export function TurnBlock({ entry, mine, waitHint, onCancel }: TurnBlockProps) {
  const t = useT().chat.turn;
  const { turn } = entry;
  const shown = (entry.prompt ? userText(entry.prompt) : undefined) ?? mine?.text ?? plain(entry.echo);
  return (
    <section className="space-y-3" data-testid="chat-turn" data-status={turn.status} aria-label={cliLabel(turn)}>
      {shown !== undefined ? <UserMessage text={shown} delivery={deliveryOf(entry, mine)} error={mine?.error} /> : null}
      <div className="flex flex-wrap items-center justify-end gap-2 text-[11.5px] text-gray-500">
        <Pill tone={TONE[turn.status]}>{t.status[turn.status]}</Pill>
        <span className="font-mono">{cliLabel(turn)}</span>
        {ACTIVE.includes(turn.status) && onCancel ? (
          <button type="button" onClick={onCancel} className="text-[12px] font-medium text-red hover:underline">
            {t.cancel}
          </button>
        ) : null}
      </div>
      {turn.status === "queued" && turn.run_id === null ? <p className="m-0 text-end text-[12px] text-gray-500">{waitHint}</p> : null}
      <TurnProgress entry={entry} />
      {entry.replies.map((e) => {
        const text = agentText(e);
        return text !== undefined ? (
          <AgentMessage key={`${e.segmentId}-${e.seq}`} text={text} footer={cliLabel(turn)} />
        ) : (
          <EventRow key={`${e.segmentId}-${e.seq}-${e.direction}`} event={e} agent={cliLabel(turn)} />
        );
      })}
      {turn.status === "failed" && turn.reason ? (
        <p className="m-0 ms-11 text-[12.5px] text-red-dark" role="alert">
          {t.failed(turn.reason)}
        </p>
      ) : null}
    </section>
  );
}
